import { type ReactNode } from 'react'

import {
  UNIFORM_STAGE_VALUES,
  type ProductionOrderSummaryType,
} from '@aeris/contracts'
import { Badge, Select } from '@aeris/ui'
import { canMoveStage } from '@aeris/uniforms'

import { STAGE_META } from '../lib/production-stage-meta.tsx'
import { useStageMove } from '../lib/use-stage-move.tsx'
import { useUniformViewer } from '../lib/use-uniform-viewer.ts'

/**
 * A etapa do pedido, trocada ali mesmo — na linha da fila e na ficha (F170).
 *
 * A planilha da loja tinha a etapa como uma lista suspensa na própria célula, e
 * a produção a trocava sem abrir nada: é o gesto que mais acontece no dia. Por
 * isso é um `Select` na linha, e não um botão que abre um formulário.
 *
 * Só as etapas que a pessoa pode alcançar ficam habilitadas (F171): o designer
 * tira da arte, o operador anda na fábrica. Quem não pode mover para lugar
 * nenhum vê o selo, não uma lista toda cinza.
 */
export const ProductionStageControl = ({
  order,
  label,
}: {
  order: Pick<ProductionOrderSummaryType, 'id' | 'stage' | 'printers'>
  /** O nome acessível: "Etapa do pedido nº 3226". */
  label: string
}): ReactNode => {
  const { actor } = useUniformViewer()
  const stage = useStageMove({ manages: actor.manages })

  const reachable = UNIFORM_STAGE_VALUES.filter(
    (target) =>
      target !== order.stage && canMoveStage(actor, order.stage, target)
  )

  if (reachable.length === 0) {
    const meta = STAGE_META[order.stage]
    return (
      <Badge tone={meta.tone} icon={meta.icon}>
        {meta.label}
      </Badge>
    )
  }

  return (
    <>
      <Select
        aria-label={label}
        value={order.stage}
        disabled={stage.pendingId === order.id}
        className="h-(--control-height-sm) min-w-40"
        onClick={(event) => {
          // A linha abre a ficha ao clique; escolher a etapa não é abrir.
          event.stopPropagation()
        }}
        onChange={(event) => {
          stage.move(order, event.target.value as typeof order.stage)
        }}
      >
        {UNIFORM_STAGE_VALUES.map((target) => (
          <option
            key={target}
            value={target}
            disabled={target !== order.stage && !reachable.includes(target)}
          >
            {STAGE_META[target].label}
          </option>
        ))}
      </Select>

      {stage.error?.orderId === order.id ? (
        <p
          role="alert"
          className="mt-1 text-(length:--text-xs) text-(--danger)"
        >
          {stage.error.message}
        </p>
      ) : null}

      {stage.dialogs}
    </>
  )
}
